import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans text-sm font-medium leading-none transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-border dark:focus-visible:ring-electric-lime focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97]",
  {
    variants: {
      variant: {
        primary:
          "bg-electric-lime text-near-black border border-near-black rounded-buttons px-5 py-2.5 hover:bg-[#aedf48] hover:shadow-[0_4px_16px_rgba(190,255,80,0.35)] hover:-translate-y-0.5",
        "ghost-dark":
          "bg-pure-white/80 dark:bg-dark-surface/80 text-near-black dark:text-dark-text border border-stone/50 dark:border-dark-border rounded-buttons px-5 py-2.5 hover:bg-stone/30 dark:hover:bg-dark-card hover:-translate-y-0.5",
        "ghost-light":
          "bg-transparent text-pure-white border border-pure-white/30 rounded-buttons px-5 py-2.5 hover:bg-pure-white hover:text-near-black hover:-translate-y-0.5",
        danger:
          "bg-coral-alert/10 text-coral-alert border border-coral-alert/20 rounded-buttons px-5 py-2.5 hover:bg-coral-alert hover:text-pure-white hover:border-coral-alert hover:-translate-y-0.5",
        "icon-filled":
          "bg-stone/30 dark:bg-dark-border text-near-black dark:text-dark-text border border-transparent rounded-iconbuttons w-10 h-10 p-0 hover:bg-stone/50 dark:hover:bg-dark-surface hover:-translate-y-0.5",
        "icon-ghost":
          "bg-transparent text-near-black dark:text-dark-text border border-stone/50 dark:border-dark-border rounded-iconbuttons w-10 h-10 p-0 hover:bg-stone/30 dark:hover:bg-dark-surface hover:-translate-y-0.5",
      },
      size: {
        default: "",
        sm: "text-xs px-3 py-1.5",
        lg: "text-base px-6 py-3",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
